import { FC, useMemo, useState } from 'react'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { EChart } from 'src/charts/EChart'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import hexToRgbA from 'src/utils/hexToRgba'
import { kFormatter } from 'src/utils/math'
import styles from './messageVolumeTrends.module.scss'
import type {
  DailyPeriodResponse,
  MonthlyPeriodResponse,
  OverallPeriodResponse,
  Platform,
  WeeklyPeriodResponse,
} from 'src/services/__generated__/api'

type Period = 'overall' | 'daily' | 'weekly' | 'monthly'

type AnyPeriodResponse =
  | OverallPeriodResponse
  | DailyPeriodResponse
  | WeeklyPeriodResponse
  | MonthlyPeriodResponse

interface MessageVolumeTrendsProps {
  data?: AnyPeriodResponse
  isLoading?: boolean
  onPeriodChange?: (period: Period) => void
  period?: Period
}

const PLATFORM_COLORS: Record<string, string> = {
  Booking: '#0A4FA3',
  Expedia: '#F5D328',
  Facebook: '#6366F1',
  Instagram: '#FF6833',
  Whatsapp: '#1EA65A',
}
const DEFAULT_PLATFORM_COLOR = '#9CA3AF'

// From messageVolumeTrends.module.scss: .statValue is 20px/100%, and both
// .chartWrapper and .emptyChart are a fixed 380px — so the chart slot cannot
// shift and only needs an honest placeholder rather than a fake empty state.
const STAT_VALUE_HEIGHT = 20
const CHART_HEIGHT = 380
const CHART_RADIUS = 8
const colorForPlatform = (name: string) =>
  PLATFORM_COLORS[name] ?? DEFAULT_PLATFORM_COLOR

interface SeriesShape {
  color: string
  data: number[]
  name: string
}

interface BuiltSeries {
  categories: string[]
  series: SeriesShape[]
}

const buildSeries = (
  data: AnyPeriodResponse | undefined,
  period: Period
): BuiltSeries => {
  if (!data) return { categories: [], series: [] }

  const toSeries = (name: string, values: number[]): SeriesShape => ({
    color: colorForPlatform(name),
    data: values,
    name,
  })

  if (period === 'overall') {
    const platforms = (data as OverallPeriodResponse).platforms ?? []
    return {
      categories: ['Total'],
      series: platforms.map((p) => toSeries(p.name, [p.mentions ?? 0])),
    }
  }

  const periodData =
    (data as DailyPeriodResponse | WeeklyPeriodResponse | MonthlyPeriodResponse)
      .data ?? []

  const categories = periodData.map((entry) => {
    const e = entry as { day?: string; month?: string; week?: string }
    return e.day || e.week || e.month || ''
  })

  // Platform set is taken from the data (first-appearance order), so we never
  // render a series for a platform the backend didn't return — e.g. WhatsApp
  // when it isn't used on this page (which previously drew a flat 0-line).
  const names: string[] = []
  const seen = new Set<string>()
  periodData.forEach((entry) => {
    ;((entry.platforms ?? []) as Platform[]).forEach((p) => {
      if (p.name && !seen.has(p.name)) {
        seen.add(p.name)
        names.push(p.name)
      }
    })
  })

  const series: SeriesShape[] = names.map((name) =>
    toSeries(
      name,
      periodData.map((entry) => {
        const platforms = (entry.platforms ?? []) as Platform[]
        return platforms.find((p) => p.name === name)?.mentions ?? 0
      })
    )
  )

  return { categories, series }
}

const computeTotal = (series: SeriesShape[]) =>
  series.reduce((sum, s) => sum + s.data.reduce((a, b) => a + b, 0), 0)

const pickStat = (
  data: AnyPeriodResponse | undefined,
  kind: 'mostActive' | 'leastActive'
) => {
  if (!data) return null
  const stats = (data as { stats?: Record<string, unknown> }).stats
  if (!stats) return null
  const value = stats[kind] as { platform?: string } | null | undefined
  return value?.platform ?? null
}

export const MessageVolumeTrends: FC<MessageVolumeTrendsProps> = ({
  data,
  isLoading = false,
  period = 'overall',
  onPeriodChange,
}) => {
  const [activePeriod, setActivePeriod] = useState<Period>(period)

  const handlePeriodChange = (newPeriod: Period) => {
    setActivePeriod(newPeriod)
    onPeriodChange?.(newPeriod)
  }

  const { categories, series } = useMemo(
    () => buildSeries(data, activePeriod),
    [data, activePeriod]
  )

  const totalMessages = useMemo(() => computeTotal(series), [series])
  const mostActiveRaw = pickStat(data, 'mostActive')
  const leastActiveRaw = pickStat(data, 'leastActive')
  const mostActive = mostActiveRaw ?? 'N/A'
  const leastActive =
    leastActiveRaw && mostActiveRaw && leastActiveRaw === mostActiveRaw
      ? '-'
      : leastActiveRaw ?? 'N/A'

  // First load only. Without this the widget rendered "No message volume data"
  // and a confident "0" total while the parent's query was still in flight —
  // loading and empty are different states and must render differently.
  const isInitialLoading = isLoading && !data

  const isEmpty = !data || totalMessages === 0

  // A single x-category (e.g. Overall mode's "Total") draws as
  // isolated dots floating in the middle of the chart, which looks
  // strange. Duplicate the single point so the area chart renders
  // as a flat horizontal line/area instead.
  const renderCategories = useMemo(() => {
    if (categories.length === 1) return ['', categories[0], '']
    return categories
  }, [categories])

  const renderSeries = useMemo<SeriesShape[]>(() => {
    if (categories.length !== 1) return series
    return series.map((s) => ({
      ...s,
      data: [s.data[0] ?? 0, s.data[0] ?? 0, s.data[0] ?? 0],
    }))
  }, [categories, series])

  const chartOptions = useMemo<any>(() => {
    return {
      grid: {
        bottom: 56,
        left: 56,
        right: 24,
        top: 24,
      },
      legend: {
        bottom: 0,
        data: renderSeries.map((s) => ({
          icon: 'circle',
          name: s.name,
        })),
        icon: 'circle',
        itemGap: 32,
        itemHeight: 8,
        itemWidth: 8,
        textStyle: {
          color: '#6A7881',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
        },
      },
      series: renderSeries.map((s) => ({
        areaStyle: {
          color: {
            colorStops: [
              { color: hexToRgbA(s.color, 0.28) ?? s.color, offset: 0 },
              { color: hexToRgbA(s.color, 0.02) ?? s.color, offset: 1 },
            ],
            type: 'linear',
            x: 0,
            x2: 0,
            y: 0,
            y2: 1,
          },
        },
        data: s.data,
        emphasis: { focus: 'series' },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 2,
          color: s.color,
        },
        lineStyle: {
          color: s.color,
          width: 2,
        },
        name: s.name,
        showSymbol: true,
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        type: 'line',
      })),
      tooltip: {
        axisPointer: {
          lineStyle: {
            color: '#8080FF',
            type: 'dashed',
          },
          type: 'line',
        },
        backgroundColor: '#FFFFFF',
        borderColor: '#E6E8EE',
        borderWidth: 1,
        extraCssText:
          'box-shadow: 0 8px 24px rgba(0,0,0,0.1); border-radius: 10px;',
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return ''
          const total = params.reduce(
            (sum: number, p: any) => sum + (p.value ?? 0),
            0
          )
          const rows = params
            .map(
              (p: any) =>
                `<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:#6A7881;margin-top:4px;">
                  <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};"></span>
                  ${p.seriesName}
                  <span style="margin-left:auto;font-weight:600;color:#1C1C1E;">${p.value?.toLocaleString() ?? 0}</span>
                </div>`
            )
            .join('')
          return `<div style="padding:4px 4px 8px;min-width:160px;font-family:Roboto,sans-serif;">
                    <div style="font-size:11px;color:#6A7881;">Total Messages</div>
                    <div style="font-size:18px;font-weight:700;color:#1C1C1E;font-family:Manrope,sans-serif;">${total.toLocaleString()}</div>
                    ${rows}
                  </div>`
        },
        trigger: 'axis',
      },
      xAxis: {
        axisLabel: {
          color: '#6A7881',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
        },
        axisLine: { lineStyle: { color: '#E6E8EE' } },
        axisTick: { show: false },
        boundaryGap: false,
        data: renderCategories,
        type: 'category',
      },
      yAxis: {
        axisLabel: {
          color: '#9CA3AF',
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
          formatter: (value: number) => kFormatter(value),
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: { color: '#EEF0F4', type: 'dashed' },
        },
        type: 'value',
      },
    }
  }, [renderCategories, renderSeries])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <SectionTitle
            title="Message Volume Trends"
            tooltipContent="Track message volume trends across different platforms"
          />
        </div>

        <div className={styles.tabsContainer}>
          {(['overall', 'daily', 'weekly', 'monthly'] as Period[]).map((p) => (
            <button
              className={`${styles.tab} ${
                activePeriod === p ? styles.active : ''
              }`}
              key={p}
              onClick={() => handlePeriodChange(p)}
              type="button"
            >
              {p[0].toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <AnnouncementIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Total Messages</div>
            <div className={styles.statValue}>
              {isInitialLoading ? (
                <Skeleton height={STAT_VALUE_HEIGHT} width={96} />
              ) : (
                totalMessages.toLocaleString()
              )}
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Most Active</div>
            <div className={styles.statValue}>
              {isInitialLoading ? (
                <Skeleton height={STAT_VALUE_HEIGHT} width={88} />
              ) : (
                mostActive
              )}
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Least Active</div>
            <div className={styles.statValue}>
              {isInitialLoading ? (
                <Skeleton height={STAT_VALUE_HEIGHT} width={88} />
              ) : (
                leastActive
              )}
            </div>
          </div>
        </div>
      </div>

      {isInitialLoading ? (
        <Skeleton height={CHART_HEIGHT} radius={CHART_RADIUS} width="100%" />
      ) : isEmpty ? (
        <div className={styles.emptyChart}>No message volume data</div>
      ) : (
        <div className={styles.chartWrapper}>
          <EChart options={chartOptions} />
        </div>
      )}
    </div>
  )
}
